/* Options shared constants + helpers + mini-components.
   Consumed by: Options.jsx, OptionsRules.jsx, OptionsTradePlanner.jsx,
   OptionsTradeLog.jsx, OptionsWatchlist.jsx, OptionsAlertLog.jsx.

   Math notation:
     S       = stock price at expiration
     k_long  = bought strike  (we paid for it)
     k_short = sold strike    (we collected premium for it)
     prem    = premium per contract (debit positive, credit positive but
               kept in its own field so we can render the right label)
     qty     = number of contracts
     ×100    = options multiplier (1 contract = 100 shares)

   Every strategy is dispatched through `strategyMeta()` which returns
   the layout + label + sign rules for the planner. Adding a new
   strategy means adding one entry there and one branch in
   `computePlanner` / `expirationRows`. */

import { C } from "./_shared";

export const API = process.env.REACT_APP_BACKEND_URL || "";
export const RULES_LS_KEY = "dt_options_rules_confirmed";

export const STRATEGIES = [
  // Verticals (existing)
  "Call Debit Spread", "Put Debit Spread", "Call Credit Spread", "Put Credit Spread",
  "Iron Condor", "Covered Call", "Cash Secured Put",
  // Basics
  "Long Call", "Short Call", "Long Put", "Short Put",
  // Neutral volatility
  "Long Straddle", "Short Straddle", "Long Strangle", "Short Strangle",
  // Condors
  "Short Iron Condor", "Call Condor", "Short Call Condor", "Put Condor", "Short Put Condor",
  // Butterflies
  "Call Butterfly", "Put Butterfly", "Short Call Butterfly", "Short Put Butterfly",
  // Ladders
  "Call Ladder", "Short Call Ladder", "Put Ladder", "Short Put Ladder",
  // Ratios / Backspreads
  "Call Ratio Spread", "Put Ratio Spread", "Call Backspread", "Put Backspread",
  // Specialist
  "Strap", "Strip", "Jade Lizard", "Synthetic Call",
  "Calendar Call Spread", "Calendar Put Spread",
  "Guts", "Short Guts",
];
export const BIASES = ["Bullish", "Bearish", "Neutral"];
export const SETUP_STATUSES = ["Watching", "Waiting", "Ready", "No Trade"];
export const LIQUIDITIES = ["Very Tight", "Tight", "Wide"];

// Official Options System Rulebook — 16 rules across 4 categories.
export const RULE_CATEGORIES = [
  {
    id: "core", title: "Core Strategy",
    rules: [
      "Primary strategy: vertical debit spreads",
      "Bullish trades: call debit spread",
      "Bearish trades: put debit spread",
      "Avoid far out-of-the-money lottery options as the main plan",
    ],
  },
  {
    id: "entry", title: "Entry Rules",
    rules: [
      "Only enter after the stock confirms the trigger price",
      "Use 45–90 days to expiration where possible",
      "Buy the strike close to current price or target area; sell the nearby strike",
      "Use a limit order — never use market order",
    ],
  },
  {
    id: "exit", title: "Exit Rules",
    rules: [
      "Take profit when the spread gains 50–80%",
      "Cut loss when the spread loses 40–50%",
      "Exit if the chart invalidation level breaks",
      "Avoid holding into the final expiration week unless the spread is near max value",
    ],
  },
  {
    id: "notrade", title: "No-Trade Rules",
    rules: [
      "Skip if option bid/ask is too wide",
      "Skip if price is stuck between key moving averages",
      "Skip if you are chasing a large candle after the move already happened",
      "Skip if the setup does not match the checklist",
    ],
  },
];
export const TOTAL_RULES = RULE_CATEGORIES.reduce((a, c) => a + c.rules.length, 0);

// Tiny JSON-wrapped fetch that threads auth cookies + sets content-type.
export async function apiJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error(`${res.status}`);
  if (res.status === 204) return null;
  const t = await res.text();
  return t ? JSON.parse(t) : null;
}

// ── Strategy metadata: single source of truth for layout rules ──
// `kind`        — math + UI dispatch key
// `direction`   — call | put | both | none
// `isCredit`    — does the trader RECEIVE premium up-front?
// `premiumLabel`— input label for the premium field
// `defaultBias` — bias auto-picked when user picks this strategy
// `actionPlan`  — list of [stage, copy] rendered in the Action Plan card
export function strategyMeta(strategy) {
  switch (strategy) {
    case "Call Debit Spread":
      return {
        kind: "call_debit", direction: "call", isCredit: false,
        premiumLabel: "Max Debit", defaultBias: "Bullish",
        actionPlan: [
          ["Entry",      "Only enter if stock confirms above trigger"],
          ["Order",      "Use limit order at or below Max Debit"],
          ["Take Profit","Sell spread around 50–70% gain"],
          ["Cut Loss",   "Exit if spread loses 40–50% or chart invalidates"],
          ["No Trade",   "Skip if bid/ask is too wide"],
        ],
      };
    case "Put Debit Spread":
      return {
        kind: "put_debit", direction: "put", isCredit: false,
        premiumLabel: "Max Debit", defaultBias: "Bearish",
        actionPlan: [
          ["Entry",      "Only enter if stock confirms below trigger"],
          ["Order",      "Use limit order at or below Max Debit"],
          ["Take Profit","Sell spread around 50–70% gain"],
          ["Cut Loss",   "Exit if spread loses 40–50% or chart invalidates"],
          ["No Trade",   "Skip if bid/ask is too wide"],
        ],
      };
    case "Call Credit Spread":
      return {
        kind: "call_credit", direction: "call", isCredit: true,
        premiumLabel: "Credit Received", defaultBias: "Bearish",
        actionPlan: [
          ["Entry",      "Sell at resistance — bias bearish/neutral"],
          ["Order",      "Use limit order at or above Credit Received"],
          ["Take Profit","Close around 50% of max credit captured"],
          ["Cut Loss",   "Exit if loss reaches 1.5–2× credit received"],
          ["No Trade",   "Avoid if event risk before expiration"],
        ],
      };
    case "Put Credit Spread":
      return {
        kind: "put_credit", direction: "put", isCredit: true,
        premiumLabel: "Credit Received", defaultBias: "Bullish",
        actionPlan: [
          ["Entry",      "Sell at support — bias bullish/neutral"],
          ["Order",      "Use limit order at or above Credit Received"],
          ["Take Profit","Close around 50% of max credit captured"],
          ["Cut Loss",   "Exit if loss reaches 1.5–2× credit received"],
          ["No Trade",   "Avoid if event risk before expiration"],
        ],
      };
    case "Iron Condor":
      return {
        kind: "iron_condor", direction: "both", isCredit: true,
        premiumLabel: "Credit Received", defaultBias: "Neutral",
        actionPlan: [
          ["Entry",      "Open in low-volatility, range-bound conditions"],
          ["Strikes",    "Place short strikes outside expected range"],
          ["Take Profit","Close around 50% of max credit captured"],
          ["Cut Loss",   "Exit one side if breached or 2× credit lost"],
          ["No Trade",   "Skip during earnings or major news windows"],
        ],
      };
    case "Covered Call":
      return {
        kind: "covered_call", direction: "call", isCredit: true,
        premiumLabel: "Credit Received", defaultBias: "Neutral",
        actionPlan: [
          ["Entry",      "Sell call against existing 100+ share lot"],
          ["Strike",     "Pick OTM strike above resistance"],
          ["Take Profit","Buy back at 50–80% credit decay or roll"],
          ["Cut Loss",   "Roll up & out if stock blows past strike"],
          ["No Trade",   "Skip if dividend/earnings before expiry"],
        ],
      };
    case "Cash Secured Put":
      return {
        kind: "cash_secured_put", direction: "put", isCredit: true,
        premiumLabel: "Credit Received", defaultBias: "Bullish",
        actionPlan: [
          ["Entry",      "Sell put at price you'd happily own stock at"],
          ["Strike",     "Pick OTM strike at strong support"],
          ["Take Profit","Buy back at 50% credit decay"],
          ["Cut Loss",   "Take assignment or roll if breached"],
          ["No Trade",   "Skip if you don't want the shares"],
        ],
      };

    // ── BASICS ──────────────────────────────────────────
    case "Long Call":
      return { ...GENERIC, kind: "long_call", direction: "call", isCredit: false,
        premiumLabel: "Premium Paid", defaultBias: "Bullish",
        strikeSlots: [{ k: "k1", label: "Call Strike" }],
        legTemplate: [{ type: "call", slot: "k1", side: 1 }] };
    case "Short Call":
      return { ...GENERIC, kind: "short_call", direction: "call", isCredit: true,
        premiumLabel: "Credit Received", defaultBias: "Bearish",
        strikeSlots: [{ k: "k1", label: "Call Strike" }],
        legTemplate: [{ type: "call", slot: "k1", side: -1 }] };
    case "Long Put":
      return { ...GENERIC, kind: "long_put", direction: "put", isCredit: false,
        premiumLabel: "Premium Paid", defaultBias: "Bearish",
        strikeSlots: [{ k: "k1", label: "Put Strike" }],
        legTemplate: [{ type: "put", slot: "k1", side: 1 }] };
    case "Short Put":
      return { ...GENERIC, kind: "short_put", direction: "put", isCredit: true,
        premiumLabel: "Credit Received", defaultBias: "Bullish",
        strikeSlots: [{ k: "k1", label: "Put Strike" }],
        legTemplate: [{ type: "put", slot: "k1", side: -1 }] };

    // ── NEUTRAL VOLATILITY ──────────────────────────────
    case "Long Straddle":
      return { ...GENERIC, kind: "long_straddle", direction: "both", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Neutral",
        strikeSlots: [{ k: "k1", label: "Strike (ATM)" }],
        legTemplate: [
          { type: "call", slot: "k1", side: 1 },
          { type: "put",  slot: "k1", side: 1 },
        ] };
    case "Short Straddle":
      return { ...GENERIC, kind: "short_straddle", direction: "both", isCredit: true,
        premiumLabel: "Total Credit", defaultBias: "Neutral",
        strikeSlots: [{ k: "k1", label: "Strike (ATM)" }],
        legTemplate: [
          { type: "call", slot: "k1", side: -1 },
          { type: "put",  slot: "k1", side: -1 },
        ] };
    case "Long Strangle":
      return { ...GENERIC, kind: "long_strangle", direction: "both", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Neutral",
        strikeSlots: [{ k: "k1", label: "Put Strike (OTM)" }, { k: "k2", label: "Call Strike (OTM)" }],
        legTemplate: [
          { type: "put",  slot: "k1", side: 1 },
          { type: "call", slot: "k2", side: 1 },
        ] };
    case "Short Strangle":
      return { ...GENERIC, kind: "short_strangle", direction: "both", isCredit: true,
        premiumLabel: "Total Credit", defaultBias: "Neutral",
        strikeSlots: [{ k: "k1", label: "Put Strike (OTM)" }, { k: "k2", label: "Call Strike (OTM)" }],
        legTemplate: [
          { type: "put",  slot: "k1", side: -1 },
          { type: "call", slot: "k2", side: -1 },
        ] };

    // ── CONDORS ─────────────────────────────────────────
    case "Short Iron Condor":   // debit reverse iron condor — profits on a big move either way
      return { ...GENERIC, kind: "short_iron_condor", direction: "both", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Neutral",
        strikeSlots: [
          { k: "k1", label: "Put Buy (lower, OTM)"  },
          { k: "k2", label: "Put Sell"              },
          { k: "k3", label: "Call Sell"             },
          { k: "k4", label: "Call Buy (upper, OTM)" },
        ],
        legTemplate: [
          { type: "put",  slot: "k1", side: 1 },
          { type: "put",  slot: "k2", side: -1 },
          { type: "call", slot: "k3", side: -1 },
          { type: "call", slot: "k4", side: 1 },
        ] };
    case "Call Condor":    // 4-leg long call condor — debit, neutral
      return { ...GENERIC, kind: "call_condor", direction: "call", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Neutral",
        strikeSlots: [
          { k: "k1", label: "Long Call (lowest)"  },
          { k: "k2", label: "Short Call"          },
          { k: "k3", label: "Short Call"          },
          { k: "k4", label: "Long Call (highest)" },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: 1 },
          { type: "call", slot: "k2", side: -1 },
          { type: "call", slot: "k3", side: -1 },
          { type: "call", slot: "k4", side: 1 },
        ] };
    case "Short Call Condor":
      return { ...GENERIC, kind: "short_call_condor", direction: "call", isCredit: true,
        premiumLabel: "Total Credit", defaultBias: "Directional",
        strikeSlots: [
          { k: "k1", label: "Short Call (lowest)"  },
          { k: "k2", label: "Long Call"            },
          { k: "k3", label: "Long Call"            },
          { k: "k4", label: "Short Call (highest)" },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: -1 },
          { type: "call", slot: "k2", side: 1 },
          { type: "call", slot: "k3", side: 1 },
          { type: "call", slot: "k4", side: -1 },
        ] };
    case "Put Condor":
      return { ...GENERIC, kind: "put_condor", direction: "put", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Neutral",
        strikeSlots: [
          { k: "k1", label: "Long Put (lowest)"  },
          { k: "k2", label: "Short Put"          },
          { k: "k3", label: "Short Put"          },
          { k: "k4", label: "Long Put (highest)" },
        ],
        legTemplate: [
          { type: "put", slot: "k1", side: 1 },
          { type: "put", slot: "k2", side: -1 },
          { type: "put", slot: "k3", side: -1 },
          { type: "put", slot: "k4", side: 1 },
        ] };
    case "Short Put Condor":
      return { ...GENERIC, kind: "short_put_condor", direction: "put", isCredit: true,
        premiumLabel: "Total Credit", defaultBias: "Directional",
        strikeSlots: [
          { k: "k1", label: "Short Put (lowest)"  },
          { k: "k2", label: "Long Put"            },
          { k: "k3", label: "Long Put"            },
          { k: "k4", label: "Short Put (highest)" },
        ],
        legTemplate: [
          { type: "put", slot: "k1", side: -1 },
          { type: "put", slot: "k2", side: 1 },
          { type: "put", slot: "k3", side: 1 },
          { type: "put", slot: "k4", side: -1 },
        ] };

    // ── BUTTERFLIES ─────────────────────────────────────
    case "Call Butterfly":     // buy 1 low, sell 2 mid, buy 1 high
      return { ...GENERIC, kind: "call_butterfly", direction: "call", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Neutral",
        strikeSlots: [
          { k: "k1", label: "Long Call (lower)" },
          { k: "k2", label: "Short Calls ×2"    },
          { k: "k3", label: "Long Call (upper)" },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: 1 },
          { type: "call", slot: "k2", side: -1, multiplier: 2 },
          { type: "call", slot: "k3", side: 1 },
        ] };
    case "Put Butterfly":
      return { ...GENERIC, kind: "put_butterfly", direction: "put", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Neutral",
        strikeSlots: [
          { k: "k1", label: "Long Put (lower)" },
          { k: "k2", label: "Short Puts ×2"    },
          { k: "k3", label: "Long Put (upper)" },
        ],
        legTemplate: [
          { type: "put", slot: "k1", side: 1 },
          { type: "put", slot: "k2", side: -1, multiplier: 2 },
          { type: "put", slot: "k3", side: 1 },
        ] };
    case "Short Call Butterfly":
      return { ...GENERIC, kind: "short_call_butterfly", direction: "call", isCredit: true,
        premiumLabel: "Total Credit", defaultBias: "Volatile",
        strikeSlots: [
          { k: "k1", label: "Short Call (lower)" },
          { k: "k2", label: "Long Calls ×2"      },
          { k: "k3", label: "Short Call (upper)" },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: -1 },
          { type: "call", slot: "k2", side: 1, multiplier: 2 },
          { type: "call", slot: "k3", side: -1 },
        ] };
    case "Short Put Butterfly":
      return { ...GENERIC, kind: "short_put_butterfly", direction: "put", isCredit: true,
        premiumLabel: "Total Credit", defaultBias: "Volatile",
        strikeSlots: [
          { k: "k1", label: "Short Put (lower)" },
          { k: "k2", label: "Long Puts ×2"      },
          { k: "k3", label: "Short Put (upper)" },
        ],
        legTemplate: [
          { type: "put", slot: "k1", side: -1 },
          { type: "put", slot: "k2", side: 1, multiplier: 2 },
          { type: "put", slot: "k3", side: -1 },
        ] };

    // ── LADDERS (aka Christmas Trees) ──────────────────
    case "Call Ladder":   // buy 1 ATM call, sell 1 OTM, sell 1 further-OTM
      return { ...GENERIC, kind: "call_ladder", direction: "call", isCredit: false,
        premiumLabel: "Net Debit / Credit", defaultBias: "Bullish",
        strikeSlots: [
          { k: "k1", label: "Long Call (ATM)"        },
          { k: "k2", label: "Short Call (OTM)"       },
          { k: "k3", label: "Short Call (far OTM)"   },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: 1 },
          { type: "call", slot: "k2", side: -1 },
          { type: "call", slot: "k3", side: -1 },
        ] };
    case "Short Call Ladder":
      return { ...GENERIC, kind: "short_call_ladder", direction: "call", isCredit: true,
        premiumLabel: "Net Credit", defaultBias: "Bullish-breakout",
        strikeSlots: [
          { k: "k1", label: "Short Call (low)" },
          { k: "k2", label: "Long Call"        },
          { k: "k3", label: "Long Call"        },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: -1 },
          { type: "call", slot: "k2", side: 1 },
          { type: "call", slot: "k3", side: 1 },
        ] };
    case "Put Ladder":    // buy 1 ATM put, sell 1 OTM, sell 1 further-OTM
      return { ...GENERIC, kind: "put_ladder", direction: "put", isCredit: false,
        premiumLabel: "Net Debit / Credit", defaultBias: "Bearish",
        strikeSlots: [
          { k: "k1", label: "Long Put (ATM)"      },
          { k: "k2", label: "Short Put (OTM)"     },
          { k: "k3", label: "Short Put (far OTM)" },
        ],
        legTemplate: [
          { type: "put", slot: "k1", side: 1 },
          { type: "put", slot: "k2", side: -1 },
          { type: "put", slot: "k3", side: -1 },
        ] };
    case "Short Put Ladder":
      return { ...GENERIC, kind: "short_put_ladder", direction: "put", isCredit: true,
        premiumLabel: "Net Credit", defaultBias: "Bearish-breakdown",
        strikeSlots: [
          { k: "k1", label: "Short Put (high)" },
          { k: "k2", label: "Long Put"         },
          { k: "k3", label: "Long Put"         },
        ],
        legTemplate: [
          { type: "put", slot: "k1", side: -1 },
          { type: "put", slot: "k2", side: 1 },
          { type: "put", slot: "k3", side: 1 },
        ] };

    // ── RATIO SPREADS ──────────────────────────────────
    // Ratio 1×N: buy 1 long leg, sell N short legs at higher strike.
    case "Call Ratio Spread":
      return { ...GENERIC, kind: "call_ratio", direction: "call", isCredit: true,
        premiumLabel: "Net Credit / Debit", defaultBias: "Moderately Bullish",
        strikeSlots: [
          { k: "k1", label: "Long Call (ATM)"  },
          { k: "k2", label: "Short Calls (OTM)" },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: 1 },
          { type: "call", slot: "k2", side: -1, multiplierField: "ratio_n" },
        ],
        extraFields: ["ratio_n"] };
    case "Put Ratio Spread":
      return { ...GENERIC, kind: "put_ratio", direction: "put", isCredit: true,
        premiumLabel: "Net Credit / Debit", defaultBias: "Moderately Bearish",
        strikeSlots: [
          { k: "k1", label: "Long Put (ATM)"  },
          { k: "k2", label: "Short Puts (OTM)" },
        ],
        legTemplate: [
          { type: "put", slot: "k1", side: 1 },
          { type: "put", slot: "k2", side: -1, multiplierField: "ratio_n" },
        ],
        extraFields: ["ratio_n"] };
    case "Call Backspread":    // sell 1 low, buy N high — long-volatility upside
      return { ...GENERIC, kind: "call_backspread", direction: "call", isCredit: true,
        premiumLabel: "Net Credit / Debit", defaultBias: "Volatile-Bull",
        strikeSlots: [
          { k: "k1", label: "Short Call (lower)" },
          { k: "k2", label: "Long Calls (higher)" },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: -1 },
          { type: "call", slot: "k2", side: 1, multiplierField: "ratio_n" },
        ],
        extraFields: ["ratio_n"] };
    case "Put Backspread":
      return { ...GENERIC, kind: "put_backspread", direction: "put", isCredit: true,
        premiumLabel: "Net Credit / Debit", defaultBias: "Volatile-Bear",
        strikeSlots: [
          { k: "k1", label: "Short Put (higher)" },
          { k: "k2", label: "Long Puts (lower)"  },
        ],
        legTemplate: [
          { type: "put", slot: "k1", side: -1 },
          { type: "put", slot: "k2", side: 1, multiplierField: "ratio_n" },
        ],
        extraFields: ["ratio_n"] };

    // ── SPECIALIST ─────────────────────────────────────
    case "Strap":   // 2 calls + 1 put, same strike — leveraged long-vol with bull tilt
      return { ...GENERIC, kind: "strap", direction: "both", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Volatile-Bull",
        strikeSlots: [{ k: "k1", label: "Strike (ATM)" }],
        legTemplate: [
          { type: "call", slot: "k1", side: 1, multiplier: 2 },
          { type: "put",  slot: "k1", side: 1 },
        ] };
    case "Strip":   // 1 call + 2 puts, same strike — long-vol with bear tilt
      return { ...GENERIC, kind: "strip", direction: "both", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Volatile-Bear",
        strikeSlots: [{ k: "k1", label: "Strike (ATM)" }],
        legTemplate: [
          { type: "call", slot: "k1", side: 1 },
          { type: "put",  slot: "k1", side: 1, multiplier: 2 },
        ] };
    case "Jade Lizard":   // short put + short call spread, net credit ≥ call-spread width
      return { ...GENERIC, kind: "jade_lizard", direction: "both", isCredit: true,
        premiumLabel: "Total Credit", defaultBias: "Neutral-Bull",
        strikeSlots: [
          { k: "k1", label: "Short Put (OTM)"  },
          { k: "k2", label: "Short Call (OTM)" },
          { k: "k3", label: "Long Call (OTM, hedge)" },
        ],
        legTemplate: [
          { type: "put",  slot: "k1", side: -1 },
          { type: "call", slot: "k2", side: -1 },
          { type: "call", slot: "k3", side: 1 },
        ] };
    case "Synthetic Call":    // long stock + long put = equivalent to long call
      return { ...GENERIC, kind: "synthetic_call", direction: "put", isCredit: false,
        premiumLabel: "Put Premium Paid", defaultBias: "Bullish",
        strikeSlots: [{ k: "k1", label: "Protective Put Strike" }],
        legTemplate: [
          { type: "put", slot: "k1", side: 1 },
        ],
        extraFields: ["cost_basis"],   // share basis = effective "strike"
        hasStock: true };
    case "Calendar Call Spread":  // sell near-month, buy far-month same strike
      return { ...GENERIC, kind: "calendar_call", direction: "call", isCredit: false,
        premiumLabel: "Net Debit", defaultBias: "Neutral",
        strikeSlots: [{ k: "k1", label: "Strike (same for both)" }],
        legTemplate: [
          { type: "call", slot: "k1", side: -1, expiry: "near" },
          { type: "call", slot: "k1", side:  1, expiry: "far" },
        ],
        extraFields: ["expiration2"],
        note: "Approximation: P/L shown at NEAR expiry; far leg retains ~40% extrinsic" };
    case "Calendar Put Spread":
      return { ...GENERIC, kind: "calendar_put", direction: "put", isCredit: false,
        premiumLabel: "Net Debit", defaultBias: "Neutral",
        strikeSlots: [{ k: "k1", label: "Strike (same for both)" }],
        legTemplate: [
          { type: "put", slot: "k1", side: -1, expiry: "near" },
          { type: "put", slot: "k1", side:  1, expiry: "far" },
        ],
        extraFields: ["expiration2"],
        note: "Approximation: P/L shown at NEAR expiry; far leg retains ~40% extrinsic" };
    case "Guts":   // long ITM call + long ITM put — expensive long-vol
      return { ...GENERIC, kind: "guts", direction: "both", isCredit: false,
        premiumLabel: "Total Debit", defaultBias: "Volatile",
        strikeSlots: [
          { k: "k1", label: "Long Call (ITM, lower)" },
          { k: "k2", label: "Long Put (ITM, higher)" },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: 1 },
          { type: "put",  slot: "k2", side: 1 },
        ] };
    case "Short Guts":
      return { ...GENERIC, kind: "short_guts", direction: "both", isCredit: true,
        premiumLabel: "Total Credit", defaultBias: "Range-bound",
        strikeSlots: [
          { k: "k1", label: "Short Call (ITM, lower)" },
          { k: "k2", label: "Short Put (ITM, higher)" },
        ],
        legTemplate: [
          { type: "call", slot: "k1", side: -1 },
          { type: "put",  slot: "k2", side: -1 },
        ] };

    default:
      return strategyMeta("Call Debit Spread");
  }
}

// Default metadata shared by every generic (leg-based) strategy. Keeps the
// switch cases above readable — they only need to override direction, kind,
// strikeSlots, and legTemplate.
const GENERIC = {
  generic: true,   // marker: use computePlannerGeneric + expirationRowsGeneric
  actionPlan: [
    ["Entry",      "Enter only when setup matches the strategy's bias"],
    ["Sizing",     "Cap capital-at-risk at 5% of account"],
    ["Take Profit","Close around 50–70% of max gain"],
    ["Cut Loss",   "Exit if chart invalidation hits or P/L breaches plan"],
    ["No Trade",   "Skip if bid/ask wide or event risk before expiry"],
  ],
};

// ── Generic leg-based payoff engine (all non-legacy strategies) ──
// Resolves `meta.legTemplate` against `plan` strike slots + ratio field into a
// concrete list of legs:  [{ type: 'call'|'put', strike, side: ±1, mult: N }]
// Then `payoffAt(S, legs)` evaluates the sum of intrinsic values at expiration
// minus the net premium, multiplied by 100 × qty. Capped strategies (condors,
// butterflies) produce defined max/min P/L; uncapped (long call, ratios)
// propagate ±Infinity which the UI displays as "Unlimited".
function buildLegs(plan, meta) {
  const ratio = Math.max(2, Math.min(5, +plan.ratio_n || 2));
  const slotVal = (slot) => +plan[slot] || 0;
  const legs = (meta.legTemplate || []).map(t => ({
    type: t.type,
    strike: slotVal(t.slot),
    side: t.side,
    mult: t.multiplier || (t.multiplierField === "ratio_n" ? ratio : 1),
  }));
  return { legs, ratio, hasStock: !!meta.hasStock, stockBasis: +plan.cost_basis || 0 };
}

function intrinsic(leg, S) {
  if (leg.type === "call") return Math.max(0, S - leg.strike);
  if (leg.type === "put")  return Math.max(0, leg.strike - S);
  return 0;
}

function payoffAt(S, built, netPrem, qty, isCredit) {
  const perContract = built.legs.reduce(
    (acc, leg) => acc + leg.side * leg.mult * intrinsic(leg, S),
    0,
  );
  // Stock component — only Synthetic Call carries it.
  const stockPL = built.hasStock && built.stockBasis > 0
    ? (S - built.stockBasis)
    : 0;
  const perShare = perContract + stockPL;
  const netPremDollars = netPrem * 100 * qty * (isCredit ? 1 : -1);
  return perShare * 100 * qty + netPremDollars;
}

function computePlannerGeneric(plan, meta) {
  const built = buildLegs(plan, meta);
  const qty = +plan.qty || 1;
  const prem = +plan.max_debit || 0;   // shared field; label set by meta.premiumLabel
  const isCredit = !!meta.isCredit;

  let dte = 0;
  if (plan.expiration) {
    const exp = new Date(plan.expiration + "T16:00:00-05:00");
    dte = Math.max(0, Math.round((exp - new Date()) / 86400000));
  }

  const out = {
    kind: meta.kind, premiumLabel: meta.premiumLabel, dte,
    spreadWidth: 0, capitalAtRisk: 0, maxValue: 0, maxProfit: 0,
    breakeven: 0, breakeven2: 0, rr: 0,
    pt50: 0, pt70: 0, sl40: 0, sl50: 0,
    premium: prem,
    uncapped: false,
  };

  // Sample payoff over a wide range → max/min.
  const strikes = built.legs.map(l => l.strike).filter(Boolean);
  if (!strikes.length) return out;
  const lo = Math.min(...strikes);
  const hi = Math.max(...strikes);
  const span = Math.max(hi - lo, hi * 0.3, 10);
  const low  = Math.max(0.01, lo - span);
  const high = hi + span;
  const steps = 200;
  const step  = (high - low) / steps;
  let maxPL = -Infinity;
  let minPL = Infinity;
  let crosses = [];
  let prev = null;
  for (let i = 0; i <= steps; i++) {
    const s = low + step * i;
    const pl = payoffAt(s, built, prem, qty, isCredit);
    if (pl > maxPL) maxPL = pl;
    if (pl < minPL) minPL = pl;
    if (prev) {
      if ((prev.pl <= 0 && pl >= 0) || (prev.pl >= 0 && pl <= 0)) {
        const t = prev.pl === pl ? 0 : (0 - prev.pl) / (pl - prev.pl);
        crosses.push(+(prev.s + t * (s - prev.s)).toFixed(4));
      }
    }
    prev = { s, pl };
  }

  // Check for unbounded payoff by probing a price way above/below.
  const farUp   = payoffAt(high + span * 10, built, prem, qty, isCredit);
  const farDown = payoffAt(Math.max(0.01, low - span * 10), built, prem, qty, isCredit);
  if (farUp > maxPL * 1.5) { maxPL = Infinity; out.uncapped = true; }
  if (farDown > maxPL * 1.5) { maxPL = Infinity; out.uncapped = true; }
  if (farUp < minPL * 1.5 || farDown < minPL * 1.5) { minPL = -Infinity; out.uncapped = true; }

  out.maxProfit = maxPL;
  out.capitalAtRisk = Math.abs(Math.min(0, minPL));
  out.maxValue = Math.max(Math.abs(maxPL), Math.abs(minPL));
  out.spreadWidth = hi - lo;
  if (crosses.length >= 1) out.breakeven  = crosses[0];
  if (crosses.length >= 2) out.breakeven2 = crosses[crosses.length - 1];
  out.rr = (out.capitalAtRisk > 0 && Number.isFinite(out.maxProfit))
    ? (out.maxProfit / out.capitalAtRisk) : 0;
  const baseline = meta.isCredit ? (Number.isFinite(out.maxProfit) ? out.maxProfit : 0) : out.capitalAtRisk;
  out.pt50 = baseline * 0.5;
  out.pt70 = baseline * 0.7;
  out.sl40 = baseline * 0.4;
  out.sl50 = baseline * 0.5;
  return out;
}

function expirationRowsGeneric(plan, meta) {
  const built = buildLegs(plan, meta);
  const qty = +plan.qty || 1;
  const prem = +plan.max_debit || 0;
  const isCredit = !!meta.isCredit;
  const current = +plan.current_price || 0;
  const strikes = built.legs.map(l => l.strike).filter(Boolean);
  if (!strikes.length) return [];

  const lo = Math.min(...strikes);
  const hi = Math.max(...strikes);
  const pad = Math.max(6, (hi - lo) * 0.8);
  const rangeLo = Math.max(0.5, Math.floor(lo - pad));
  const rangeHi = Math.ceil(hi + pad);
  const step = Math.max(0.5, Math.round((rangeHi - rangeLo) / 18 * 2) / 2);

  const samples = new Set();
  for (let s = rangeLo; s <= rangeHi; s += step) samples.add(+s.toFixed(2));
  for (const a of strikes) samples.add(+a.toFixed(2));
  if (current > 0) samples.add(+current.toFixed(2));

  // Marker: highlight every strike in the leg set with leg kind + side.
  const markerStrikes = {};
  const COLORS = {
    "call_1": { label: "← Long Call",  color: C.blue,  bg: "#eff6ff" },
    "call_-1":{ label: "← Short Call", color: C.amber, bg: C.ab      },
    "put_1":  { label: "← Long Put",   color: C.blue,  bg: "#eff6ff" },
    "put_-1": { label: "← Short Put",  color: C.red,   bg: C.rb      },
  };
  for (const l of built.legs) {
    const key = +(+l.strike).toFixed(2);
    markerStrikes[key] = COLORS[`${l.type}_${l.side}`] || { label: "← Strike", color: C.gray, bg: "#f9fafb" };
  }
  const currentKey = current > 0 ? +current.toFixed(2) : null;

  const rawRows = [];
  for (const s of [...samples].sort((a, b) => a - b)) {
    const netPL = payoffAt(s, built, prem, qty, isCredit);
    const longSum = built.legs.filter(l => l.side === 1)
      .reduce((a, l) => a + l.mult * intrinsic(l, s), 0) * 100 * qty;
    const shortSum = built.legs.filter(l => l.side === -1)
      .reduce((a, l) => a + l.mult * intrinsic(l, s), 0) * 100 * qty;
    const cost = prem * 100 * qty;
    const ror = cost > 0 ? (netPL / cost) * 100 : 0;
    const marker = markerStrikes[+s.toFixed(2)] || null;
    const isCurrent = currentKey != null && Math.abs(s - currentKey) < 0.01;
    rawRows.push({
      s,
      longVal:  longSum,
      shortVal: shortSum,
      spreadValue: longSum - shortSum,
      netPL, cost, ror, marker, isCurrent, isMarker: !!marker,
    });
  }

  // Collapse consecutive identical-P/L rows.
  const rows = [];
  for (const r of rawRows) {
    const last = rows[rows.length - 1];
    if (last && !r.isMarker && !r.isCurrent && !last.isMarker && !last.isCurrent
        && Math.abs(last.netPL - r.netPL) < 0.01) {
      last.sHi = r.s;
      last.isRange = true;
    } else {
      rows.push({ ...r, sHi: r.s, isRange: false });
    }
  }
  return rows;
}

// ── Planner math ─────────────────────────────────────────
// Returns trade math object used by the Trade Math card. Different
// strategies expose different inputs — premiumLabel changes from
// "Max Debit" to "Credit Received" and breakeven is single (vertical)
// or double (Iron Condor) accordingly.
export function computePlanner(p) {
  const m = strategyMeta(p.strategy);
  if (m.generic) return computePlannerGeneric(p, m);
  const qty = +p.qty || 1;
  const debit  = +p.max_debit || 0;        // for debit spreads
  const credit = +p.max_debit || 0;        // input field is shared, label switches
  // Credit strategies put the credit value in the same `max_debit` field
  // — kept this way so we don't multiply state branches.

  let dte = 0;
  if (p.expiration) {
    const exp = new Date(p.expiration + "T16:00:00-05:00");
    const now = new Date();
    dte = Math.max(0, Math.round((exp - now) / (24 * 3600 * 1000)));
  }

  const out = {
    kind: m.kind, premiumLabel: m.premiumLabel,
    spreadWidth: 0, capitalAtRisk: 0, maxValue: 0, maxProfit: 0,
    breakeven: 0, breakeven2: 0, rr: 0, dte,
    pt50: 0, pt70: 0, sl40: 0, sl50: 0,
    premium: m.isCredit ? credit : debit,
  };

  if (m.kind === "call_debit" || m.kind === "put_debit") {
    const buy = +p.buy_strike || 0;
    const sell = +p.sell_strike || 0;
    out.spreadWidth = Math.abs(sell - buy);
    out.capitalAtRisk = debit * 100 * qty;
    out.maxValue = out.spreadWidth * 100 * qty;
    out.maxProfit = Math.max(0, out.maxValue - out.capitalAtRisk);
    out.breakeven = m.kind === "call_debit" ? (buy + debit) : (buy - debit);
  } else if (m.kind === "call_credit" || m.kind === "put_credit") {
    const buy = +p.buy_strike || 0;
    const sell = +p.sell_strike || 0;
    out.spreadWidth = Math.abs(buy - sell);
    out.maxProfit = credit * 100 * qty;
    out.capitalAtRisk = Math.max(0, (out.spreadWidth - credit) * 100 * qty);
    out.maxValue = out.spreadWidth * 100 * qty;
    out.breakeven = m.kind === "call_credit" ? (sell + credit) : (sell - credit);
  } else if (m.kind === "iron_condor") {
    const pb = +p.put_buy_strike || 0;
    const ps = +p.put_sell_strike || 0;
    const cs = +p.call_sell_strike || 0;
    const cb = +p.call_buy_strike || 0;
    const putWing  = Math.abs(ps - pb);
    const callWing = Math.abs(cb - cs);
    out.spreadWidth = Math.max(putWing, callWing);
    out.maxProfit = credit * 100 * qty;
    out.capitalAtRisk = Math.max(0, (out.spreadWidth - credit) * 100 * qty);
    out.maxValue = out.spreadWidth * 100 * qty;
    out.breakeven  = ps - credit;   // lower BE
    out.breakeven2 = cs + credit;   // upper BE
  } else if (m.kind === "covered_call") {
    const sell  = +p.sell_strike || 0;
    const basis = +p.cost_basis  || 0;
    // We hold 100×qty shares already at `basis`. Selling a call adds `credit`.
    // Max profit is hit when called away at `sell`.
    out.spreadWidth = Math.max(0, sell - basis);
    out.maxProfit = Math.max(0, (sell - basis + credit)) * 100 * qty;
    // Maximum theoretical loss = stock collapses to zero, offset by credit.
    out.capitalAtRisk = Math.max(0, (basis - credit)) * 100 * qty;
    out.maxValue = out.maxProfit + out.capitalAtRisk;
    out.breakeven = Math.max(0, basis - credit);
  } else if (m.kind === "cash_secured_put") {
    const sell = +p.sell_strike || 0;
    out.spreadWidth = sell;
    out.maxProfit = credit * 100 * qty;
    // If assigned at strike and stock → 0 we'd own (strike - credit) of loss.
    out.capitalAtRisk = Math.max(0, (sell - credit)) * 100 * qty;
    out.maxValue = out.maxProfit + out.capitalAtRisk;
    out.breakeven = Math.max(0, sell - credit);
  }

  out.rr = out.capitalAtRisk > 0 ? (out.maxProfit / out.capitalAtRisk) : 0;
  // Profit / loss thresholds (% of capital at risk for debit-style, % of credit for credit-style)
  // — both share the same UX surface: "what dollar P/L corresponds to 50%/70%/40%/50%?"
  const baseline = m.isCredit ? out.maxProfit : out.capitalAtRisk;
  out.pt50 = baseline * 0.5;
  out.pt70 = baseline * 0.7;
  out.sl40 = baseline * 0.4;
  out.sl50 = baseline * 0.5;
  return out;
}

// ── Expiration P/L Table ─────────────────────────────────
// Returns the dollar P/L at expiration across a sampled stock-price range.
// At expiration each leg has pure intrinsic value, so we evaluate the
// strategy's payoff function across ~18 sampled price points anchored
// around the relevant strikes + current price.
export function expirationRows(p) {
  const m = strategyMeta(p.strategy);
  if (m.generic) return expirationRowsGeneric(p, m);
  const qty = +p.qty || 1;
  const prem = +p.max_debit || 0;
  const current = +p.current_price || 0;

  // Gather the strikes that anchor this strategy's range so we can
  // include them as exact rows in the table.
  let anchors = [];
  if (m.kind === "iron_condor") {
    anchors = [+p.put_buy_strike, +p.put_sell_strike, +p.call_sell_strike, +p.call_buy_strike].filter(x => x > 0);
  } else if (m.kind === "covered_call") {
    anchors = [+p.sell_strike, +p.cost_basis].filter(x => x > 0);
  } else if (m.kind === "cash_secured_put") {
    anchors = [+p.sell_strike].filter(x => x > 0);
  } else {
    anchors = [+p.buy_strike, +p.sell_strike].filter(x => x > 0);
  }
  if (current > 0) anchors.push(current);
  if (anchors.length < 1) return [];

  const lo = Math.min(...anchors);
  const hi = Math.max(...anchors);
  const pad = Math.max(6, (hi - lo) * 0.8);
  const rangeLo = Math.floor(lo - pad);
  const rangeHi = Math.ceil(hi + pad);
  const step = Math.max(1, Math.round((rangeHi - rangeLo) / 18));

  const samples = new Set();
  for (let s = rangeLo; s <= rangeHi; s += step) samples.add(+s.toFixed(2));
  for (const a of anchors) samples.add(+a.toFixed(2));

  // Per-strategy payoff at expiration (returns net P/L in DOLLARS,
  // already multiplied by 100 × qty).
  function payoff(s) {
    if (m.kind === "call_debit") {
      const buy = +p.buy_strike || 0; const sell = +p.sell_strike || 0;
      const longVal  = Math.max(0, s - buy);
      const shortVal = Math.max(0, s - sell);
      const spreadValue = (longVal - shortVal) * 100 * qty;
      const cost = prem * 100 * qty;
      return { longVal: longVal*100*qty, shortVal: shortVal*100*qty, spreadValue, netPL: spreadValue - cost, cost };
    }
    if (m.kind === "put_debit") {
      const buy = +p.buy_strike || 0; const sell = +p.sell_strike || 0;
      const longVal  = Math.max(0, buy - s);
      const shortVal = Math.max(0, sell - s);
      const spreadValue = (longVal - shortVal) * 100 * qty;
      const cost = prem * 100 * qty;
      return { longVal: longVal*100*qty, shortVal: shortVal*100*qty, spreadValue, netPL: spreadValue - cost, cost };
    }
    if (m.kind === "call_credit") {
      const buy = +p.buy_strike || 0; const sell = +p.sell_strike || 0;
      const shortVal = Math.max(0, s - sell);   // we sold this leg
      const longVal  = Math.max(0, s - buy);    // we bought this leg
      const spreadDebt = (shortVal - longVal) * 100 * qty;
      const credit = prem * 100 * qty;
      return { longVal: longVal*100*qty, shortVal: shortVal*100*qty, spreadValue: -spreadDebt, netPL: credit - spreadDebt, cost: credit };
    }
    if (m.kind === "put_credit") {
      const buy = +p.buy_strike || 0; const sell = +p.sell_strike || 0;
      const shortVal = Math.max(0, sell - s);
      const longVal  = Math.max(0, buy - s);
      const spreadDebt = (shortVal - longVal) * 100 * qty;
      const credit = prem * 100 * qty;
      return { longVal: longVal*100*qty, shortVal: shortVal*100*qty, spreadValue: -spreadDebt, netPL: credit - spreadDebt, cost: credit };
    }
    if (m.kind === "iron_condor") {
      const pb = +p.put_buy_strike || 0;
      const ps = +p.put_sell_strike || 0;
      const cs = +p.call_sell_strike || 0;
      const cb = +p.call_buy_strike || 0;
      const shortPut = Math.max(0, ps - s);
      const longPut  = Math.max(0, pb - s);
      const shortCall= Math.max(0, s - cs);
      const longCall = Math.max(0, s - cb);
      const putSpreadDebt  = (shortPut - longPut)   * 100 * qty;
      const callSpreadDebt = (shortCall - longCall) * 100 * qty;
      const credit = prem * 100 * qty;
      const totalDebt = putSpreadDebt + callSpreadDebt;
      return {
        longVal: (longPut + longCall) * 100 * qty,
        shortVal:(shortPut + shortCall)* 100 * qty,
        spreadValue: -totalDebt,
        netPL: credit - totalDebt,
        cost: credit,
      };
    }
    if (m.kind === "covered_call") {
      const sell  = +p.sell_strike || 0;
      const basis = +p.cost_basis  || 0;
      // We own 100×qty shares at basis. We sold 1 call per qty.
      // At expiration: shareValue = min(s, sell) since called away above strike.
      const cappedShare = Math.min(s, sell);
      const stockPL  = (cappedShare - basis) * 100 * qty;
      const credit   = prem * 100 * qty;
      const callIntrinsic = Math.max(0, s - sell) * 100 * qty;
      return {
        longVal: cappedShare * 100 * qty,            // capped share value
        shortVal: callIntrinsic,                     // call we sold (we owe this)
        spreadValue: stockPL,
        netPL: stockPL + credit,
        cost: credit,
      };
    }
    if (m.kind === "cash_secured_put") {
      const sell = +p.sell_strike || 0;
      const putIntrinsic = Math.max(0, sell - s) * 100 * qty;
      const credit = prem * 100 * qty;
      return {
        longVal: 0,
        shortVal: putIntrinsic,
        spreadValue: -putIntrinsic,
        netPL: credit - putIntrinsic,
        cost: credit,
      };
    }
    return { longVal: 0, shortVal: 0, spreadValue: 0, netPL: 0, cost: 0 };
  }

  // Marker mapping: which strikes do we visually highlight in the table?
  const markerStrikes = (() => {
    if (m.kind === "iron_condor") {
      return {
        [+(+p.put_buy_strike  || 0).toFixed(2)]:  { label: "← Put buy",   color: C.blue,   bg: "#eff6ff" },
        [+(+p.put_sell_strike || 0).toFixed(2)]:  { label: "← Put sell",  color: C.amber,  bg: C.ab },
        [+(+p.call_sell_strike|| 0).toFixed(2)]:  { label: "← Call sell", color: C.amber,  bg: C.ab },
        [+(+p.call_buy_strike || 0).toFixed(2)]:  { label: "← Call buy",  color: C.blue,   bg: "#eff6ff" },
      };
    }
    if (m.kind === "covered_call") {
      return {
        [+(+p.cost_basis   || 0).toFixed(2)]: { label: "← Cost basis", color: C.gray, bg: "#f9fafb" },
        [+(+p.sell_strike  || 0).toFixed(2)]: { label: "← Call sell",  color: C.amber, bg: C.ab },
      };
    }
    if (m.kind === "cash_secured_put") {
      return { [+(+p.sell_strike || 0).toFixed(2)]: { label: "← Put sell", color: C.amber, bg: C.ab } };
    }
    return {
      [+(+p.buy_strike  || 0).toFixed(2)]: { label: "← Buy strike",  color: C.blue,  bg: "#eff6ff" },
      [+(+p.sell_strike || 0).toFixed(2)]: { label: "← Sell strike", color: C.amber, bg: C.ab },
    };
  })();
  const currentKey = current > 0 ? +current.toFixed(2) : null;

  const rawRows = [];
  for (const s of [...samples].sort((a, b) => a - b)) {
    const o = payoff(s);
    const ror = o.cost > 0 ? (o.netPL / o.cost) * 100 : 0;
    const marker = markerStrikes[+s.toFixed(2)] || null;
    const isCurrent = currentKey != null && Math.abs(s - currentKey) < 0.01;
    rawRows.push({
      s, ...o, ror, marker,
      isCurrent,
      isMarker: !!marker,
    });
  }

  // Collapse consecutive identical-P/L rows into a single range row
  // (skipping rows that carry a marker — those must always render).
  const rows = [];
  for (const r of rawRows) {
    const last = rows[rows.length - 1];
    if (last && !r.isMarker && !r.isCurrent && !last.isMarker && !last.isCurrent
        && Math.abs(last.netPL - r.netPL) < 0.01) {
      last.sHi  = r.s;
      last.isRange = true;
    } else {
      rows.push({ ...r, sHi: r.s, isRange: false });
    }
  }
  return rows;
}

// Stock-price-tier strike increment (5 above $100, 1 above $20, else $0.5).
export function strikeIncrement(price) {
  if (price > 100) return 5;
  if (price > 20)  return 1;
  return 0.5;
}

// Next Friday approximately 50 trading days out — default expiration.
export function nextExpiration() {
  const d = new Date();
  d.setDate(d.getDate() + 50);
  while (d.getDay() !== 5) d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function fmtStrike(v, inc) { return v.toFixed(inc >= 1 ? 0 : 2); }

// Given live market ctx + an explicit strategy, produce a full set of
// suggested trade-setup values. If `strategy` is omitted we infer the
// best-fit strategy from bias.
export function computeAutoFill(ctx, strategy) {
  const price = ctx.price || 0;
  if (!price) return {};
  const inc = strikeIncrement(price);
  const bias = (price > ctx.dma20 && ctx.dma20 > ctx.dma50) ? "Bullish"
             : (price < ctx.dma20 && ctx.dma20 < ctx.dma50) ? "Bearish"
             : "Bullish";
  const chosenStrategy = strategy || (bias === "Bullish" ? "Call Debit Spread" : "Put Debit Spread");
  const m = strategyMeta(chosenStrategy);

  // Smart entry-trigger logic (identical for every strategy — about confirmation).
  let trigger = 0, entry_method = "10-day high";
  const within = (high) => high > 0 && (high - price) / price <= 0.03;
  if (within(ctx.high10))      { trigger = ctx.high10; entry_method = "10-day high"; }
  else if (within(ctx.high5))  { trigger = ctx.high5;  entry_method = "5-day high"; }
  else                          { trigger = price * 1.01; entry_method = "Price +1%"; }
  if (!Number.isFinite(trigger) || trigger <= 0) {
    trigger = price * 1.01; entry_method = "Price +1%";
  }
  const invalidation = ctx.low10 > 0 ? ctx.low10 : (price - inc * 4);

  // Strikes vary by strategy. Defaults below pick reasonable values that
  // a human trader can fine-tune in the UI.
  const baseFields = {
    bias: m.defaultBias === "Neutral" ? bias : m.defaultBias,
    strategy: chosenStrategy,
    current_price: price.toFixed(2),
    entry_trigger: trigger.toFixed(2),
    entry_method,
    invalidation: invalidation.toFixed(2),
    expiration:  nextExpiration(),
    qty:         "1",
  };

  if (m.kind === "call_debit") {
    const buyStrike  = Math.ceil(price / inc) * inc;
    const sellStrike = buyStrike + inc;
    return { ...baseFields,
      buy_strike: fmtStrike(buyStrike, inc), sell_strike: fmtStrike(sellStrike, inc),
      max_debit: ((sellStrike - buyStrike) * 0.45).toFixed(2),
    };
  }
  if (m.kind === "put_debit") {
    const buyStrike  = Math.floor(price / inc) * inc;
    const sellStrike = buyStrike - inc;
    return { ...baseFields,
      buy_strike: fmtStrike(buyStrike, inc), sell_strike: fmtStrike(sellStrike, inc),
      max_debit: ((buyStrike - sellStrike) * 0.45).toFixed(2),
    };
  }
  if (m.kind === "call_credit") {
    // Bear call: sell ~10% above current price, buy further-OTM hedge.
    // Wider OTM than the prior 2-strike approach matches what real
    // 30-45 DTE bear-call setups look like (~30-40 delta on the short
    // leg) and gives the underlying room to oscillate before testing
    // the short strike.
    const sellStrike = Math.ceil((price * 1.10) / inc) * inc;
    const buyStrike  = sellStrike + inc;
    return { ...baseFields,
      sell_strike: fmtStrike(sellStrike, inc), buy_strike: fmtStrike(buyStrike, inc),
      max_debit: ((buyStrike - sellStrike) * 0.30).toFixed(2),
    };
  }
  if (m.kind === "put_credit") {
    // Bull put: sell ~10% below current price (well below support),
    // buy further-OTM hedge. The 10% buffer matches a typical 25-30 delta
    // short put on a 45-DTE bull-put credit spread and dramatically
    // reduces tested-strike risk vs. the old 2-strike-OTM default.
    const sellStrike = Math.floor((price * 0.90) / inc) * inc;
    const buyStrike  = sellStrike - inc;
    return { ...baseFields,
      sell_strike: fmtStrike(sellStrike, inc), buy_strike: fmtStrike(buyStrike, inc),
      max_debit: ((sellStrike - buyStrike) * 0.30).toFixed(2),
    };
  }
  if (m.kind === "iron_condor") {
    const ps = Math.floor(price / inc) * inc - inc * 2;
    const pb = ps - inc;
    const cs = Math.ceil (price / inc) * inc + inc * 2;
    const cb = cs + inc;
    const widerWing = Math.max(ps - pb, cb - cs);
    return { ...baseFields,
      put_buy_strike:   fmtStrike(pb, inc),
      put_sell_strike:  fmtStrike(ps, inc),
      call_sell_strike: fmtStrike(cs, inc),
      call_buy_strike:  fmtStrike(cb, inc),
      max_debit: (widerWing * 0.30).toFixed(2),
    };
  }
  if (m.kind === "covered_call") {
    const sellStrike = Math.ceil(price / inc) * inc + inc;   // 1 strike OTM
    return { ...baseFields,
      sell_strike: fmtStrike(sellStrike, inc),
      cost_basis:  price.toFixed(2),
      max_debit: (price * 0.015).toFixed(2),    // ~1.5% of share price typical
    };
  }
  if (m.kind === "cash_secured_put") {
    const sellStrike = Math.floor(price / inc) * inc - inc;  // 1 strike OTM
    return { ...baseFields,
      sell_strike: fmtStrike(sellStrike, inc),
      max_debit: (price * 0.015).toFixed(2),
    };
  }

  // ── Generic strategies: compute strike slots based on ATM + 1-4 increments ──
  if (m.generic && Array.isArray(m.strikeSlots)) {
    const atm = Math.round(price / inc) * inc;
    const slotValues = {};
    // Populate strikes based on category — keep defaults sensible for each layout.
    const n = m.strikeSlots.length;
    const isPutSide  = (m.direction === "put");
    const isCallSide = (m.direction === "call");
    if (n === 1) {
      slotValues.k1 = fmtStrike(atm, inc);
    } else if (n === 2) {
      // Two strikes: straddle variants use ATM + 2-inc OTM each way;
      // ratio/backspreads space further.
      if (m.kind.includes("strangle") || m.kind.includes("guts")) {
        slotValues.k1 = fmtStrike(atm - inc * 2, inc);   // put side
        slotValues.k2 = fmtStrike(atm + inc * 2, inc);   // call side
        if (m.kind === "guts" || m.kind === "short_guts") {
          // Guts uses ITM strikes (call lower, put higher).
          slotValues.k1 = fmtStrike(atm - inc * 2, inc);
          slotValues.k2 = fmtStrike(atm + inc * 2, inc);
        }
      } else if (m.kind.includes("ratio") || m.kind.includes("backspread")) {
        if (isCallSide) {
          slotValues.k1 = fmtStrike(atm, inc);
          slotValues.k2 = fmtStrike(atm + inc * 2, inc);
        } else if (isPutSide) {
          slotValues.k1 = fmtStrike(atm, inc);
          slotValues.k2 = fmtStrike(atm - inc * 2, inc);
        }
      } else {
        slotValues.k1 = fmtStrike(atm, inc);
        slotValues.k2 = fmtStrike(atm + inc, inc);
      }
    } else if (n === 3) {
      // Butterflies + ladders + jade lizard.
      if (m.kind.includes("butterfly")) {
        slotValues.k1 = fmtStrike(atm - inc, inc);
        slotValues.k2 = fmtStrike(atm, inc);
        slotValues.k3 = fmtStrike(atm + inc, inc);
      } else if (m.kind.includes("ladder")) {
        if (isCallSide) {
          slotValues.k1 = fmtStrike(atm, inc);
          slotValues.k2 = fmtStrike(atm + inc, inc);
          slotValues.k3 = fmtStrike(atm + inc * 2, inc);
        } else {
          slotValues.k1 = fmtStrike(atm, inc);
          slotValues.k2 = fmtStrike(atm - inc, inc);
          slotValues.k3 = fmtStrike(atm - inc * 2, inc);
        }
      } else if (m.kind === "jade_lizard") {
        slotValues.k1 = fmtStrike(atm - inc * 2, inc);   // short put
        slotValues.k2 = fmtStrike(atm + inc, inc);       // short call
        slotValues.k3 = fmtStrike(atm + inc * 2, inc);   // long call
      }
    } else if (n === 4) {
      // Condors: k1 < k2 < k3 < k4.
      slotValues.k1 = fmtStrike(atm - inc * 2, inc);
      slotValues.k2 = fmtStrike(atm - inc, inc);
      slotValues.k3 = fmtStrike(atm + inc, inc);
      slotValues.k4 = fmtStrike(atm + inc * 2, inc);
    }
    const extra = {};
    if ((m.extraFields || []).includes("ratio_n")) extra.ratio_n = "2";
    if ((m.extraFields || []).includes("expiration2")) {
      const d = new Date(); d.setDate(d.getDate() + 80);
      while (d.getDay() !== 5) d.setDate(d.getDate() + 1);
      extra.expiration2 = d.toISOString().slice(0, 10);
    }
    if ((m.extraFields || []).includes("cost_basis")) extra.cost_basis = price.toFixed(2);
    // Heuristic premium estimate: widest strike gap × 0.35 for debit strategies;
    // 0.25 × narrowest wing for credit strategies. Users override easily.
    const sortedStrikes = Object.values(slotValues).map(v => +v).filter(Boolean).sort((a, b) => a - b);
    const maxGap = sortedStrikes.length ? (sortedStrikes[sortedStrikes.length - 1] - sortedStrikes[0]) : inc;
    const premEst = m.isCredit ? (maxGap * 0.25).toFixed(2) : (maxGap * 0.35).toFixed(2);
    return { ...baseFields, ...slotValues, ...extra, max_debit: premEst };
  }
  return baseFields;
}

// ── Mini input helpers ───────────────────────────────────
export function F({ label, value, onChange, type = "text", placeholder = "", testid }) {
  // Mobile numeric keyboards: type="number" alone shows the full keyboard
  // on some Androids; pairing with inputMode="decimal" guarantees the
  // numeric keypad on iOS + Android. type="date" is left alone.
  const extra = type === "number" ? { inputMode: "decimal" } : {};
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <label style={{ fontSize: 10, color: C.gray, fontWeight: 500 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testid} {...extra}
        style={{ padding: "7px 9px", borderRadius: 6, border: "0.5px solid #d1d5db", fontSize: 12, background: "#fff" }} />
    </div>
  );
}

export function S({ label, value, onChange, options, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <label style={{ fontSize: 10, color: C.gray, fontWeight: 500 }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        style={{ padding: "7px 9px", borderRadius: 6, border: "0.5px solid #d1d5db", fontSize: 12, background: "#fff" }}>
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

export function Stat({ label, value, color }) {
  return (
    <div style={{ background: "#f9fafb", borderRadius: 7, padding: "8px 10px" }}>
      <div style={{ fontSize: 10, color: C.gray, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: color || "#111" }}>{value}</div>
    </div>
  );
}
